/**
 * Título editable del expediente.
 *
 * Click en "editar" → input inline con validación 3-200 chars.
 * Sprint 18.4.2 — extraído del orchestrator.
 */

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { updateProject, type ProjectDetailDTO } from "@/lib/api";
import { useActiveMunicipalityDane } from "@/lib/municipality";

export function EditableTitle({
  project,
  onChanged,
}: {
  project: ProjectDetailDTO;
  onChanged: () => void;
}) {
  const activeDane = useActiveMunicipalityDane();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(project.name);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (value: string) => updateProject(project.id, activeDane, { name: value }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Error al renombrar.");
    },
  });

  const trimmed = draft.trim();
  const isValid = trimmed.length >= 3 && trimmed.length <= 200;

  if (!editing) {
    return (
      <div className="group flex flex-wrap items-center gap-2">
        <h1
          className="text-2xl font-extrabold tracking-tight text-slate-900"
          data-testid="project-name"
        >
          {project.name}
        </h1>
        <button
          type="button"
          onClick={() => {
            setDraft(project.name);
            setEditing(true);
          }}
          className="text-xs font-medium text-slate-400 transition hover:text-brand-700"
          data-testid="project-name-edit"
          aria-label="Editar nombre"
        >
          editar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        maxLength={200}
        data-testid="project-name-input"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-2xl font-extrabold tracking-tight text-slate-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => mutation.mutate(trimmed)}
          disabled={!isValid || mutation.isPending}
          data-testid="project-name-save"
          className="rounded-md bg-brand-700 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {mutation.isPending ? "Guardando…" : "Guardar"}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(project.name);
            setError(null);
          }}
          className="text-xs font-medium text-slate-500 hover:text-slate-700"
        >
          Cancelar
        </button>
        <span className="text-[11px] text-slate-500">{trimmed.length}/200 · mín 3</span>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
